/**
 * FlagHistorySection
 *
 * Lists every flag ever filed against a user. Two modes:
 *   - default (readOnly=false)  → super admins see Clear / Mark Compromised
 *                                 controls on open flags. Used inside the
 *                                 Flag Inbox dashboard's per-user expand.
 *   - readOnly=true             → history-only view, no actions. Used in
 *                                 UserDetailModal — admins read the history
 *                                 in context, then triage from the dedicated
 *                                 Flag Inbox page.
 */

import React, { useEffect, useState } from 'react';
import { FiFlag, FiCheck, FiAlertOctagon } from 'react-icons/fi';
import { userFlagsAPI } from '../../services/api';
import Button from '../common/Button';

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return String(d);
  }
}

function StatusPill({ status }) {
  const map = {
    open: { bg: '#f3ebd7', color: '#8a6a1f', label: 'OPEN' },
    cleared: { bg: '#e8efe5', color: '#4a6b3f', label: 'CLEARED' },
    compromised: { bg: '#f2e9e6', color: '#9a564a', label: 'COMPROMISED' },
  };
  const s = map[status] || map.open;
  return (
    <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[9.5px] font-bold" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

export default function FlagHistorySection({ userId, currentUserRole, onUserUpdated, readOnly = false }) {
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyFlag, setBusyFlag] = useState(null); // flagId currently being acted on
  const [showCompromiseConfirm, setShowCompromiseConfirm] = useState(null); // flag pending confirm
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const isSuperAdmin = currentUserRole === 'super_admin';

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await userFlagsAPI.listForUser(userId);
      setFlags(res?.data?.flags || []);
    } catch (err) {
      setFlags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const clear = async (flagId) => {
    setBusyFlag(flagId);
    setError('');
    try {
      await userFlagsAPI.clear(flagId, notes || undefined);
      setNotes('');
      load();
    } catch (err) {
      setError(err?.message || 'Failed to clear flag');
    } finally {
      setBusyFlag(null);
    }
  };

  const compromise = async (flagId) => {
    setBusyFlag(flagId);
    setError('');
    try {
      const res = await userFlagsAPI.markCompromised(flagId, notes || undefined);
      setNotes('');
      setShowCompromiseConfirm(null);
      if (res?.success && typeof onUserUpdated === 'function') onUserUpdated(res.data?.user);
      load();
    } catch (err) {
      setError(err?.message || 'Failed to mark compromised');
    } finally {
      setBusyFlag(null);
    }
  };

  if (loading) {
    return (
      <div className="text-[11.5px] italic text-[#7a7060] animate-pulse">Loading flag history…</div>
    );
  }
  if (!flags || flags.length === 0) {
    return (
      <div className="text-[11.5px] italic text-[#7a7060]">No flags filed against this user.</div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="text-[11.5px] px-3 py-2 rounded-lg border" style={{ background: '#f2e9e6', borderColor: '#e6d2cb', color: '#9a564a' }}>{error}</div>
      )}

      {flags.map((f) => {
        const open = f.status === 'open';
        return (
          <div
            key={f._id}
            className="border rounded-[10px] overflow-hidden"
            style={{ borderColor: '#e8e2d6', background: '#fdfcf9' }}
          >
            <div className="px-3 py-2 flex items-start gap-2 border-b" style={{ borderColor: '#e8e2d6' }}>
              <FiFlag className="text-[14px] mt-0.5 shrink-0" style={{ color: open ? '#b8973a' : '#9a9080' }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill status={f.status} />
                  <span className="text-[10.5px] text-[#7a7060]">
                    Filed by <strong className="text-[#08111f]">{f.reporterName || 'unknown'}</strong>
                    {f.reporterRole && <span className="text-[#9a9080]"> ({f.reporterRole})</span>}
                  </span>
                  <span className="text-[10.5px] text-[#7a7060]">· {fmtDate(f.createdAt)}</span>
                </div>
                <div className="text-[11.5px] text-[#08111f] mt-1 leading-snug">{f.reason}</div>
                {!open && f.resolvedByName && (
                  <div className="text-[10.5px] text-[#7a7060] mt-1.5 italic">
                    Resolved by <strong>{f.resolvedByName}</strong> on {fmtDate(f.resolvedAt)}
                    {f.resolutionNotes && <span> — {f.resolutionNotes}</span>}
                  </div>
                )}
              </div>
            </div>

            {open && isSuperAdmin && !readOnly && (
              <div className="px-3 py-2.5 space-y-2">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Resolution notes (optional)"
                  rows={2}
                  maxLength={1000}
                  className="w-full border rounded-[8px] px-2.5 py-1.5 text-[11.5px] outline-none focus:border-[#145f5f40]"
                  style={{ borderColor: '#ddd6c6' }}
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => clear(f._id)}
                    loading={busyFlag === f._id}
                    leftIcon={<FiCheck className="text-[11px]" />}
                  >
                    {busyFlag === f._id ? 'Clearing…' : 'Clear Flag'}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowCompromiseConfirm(f)}
                    disabled={busyFlag === f._id}
                    leftIcon={<FiAlertOctagon className="text-[11px]" />}
                  >
                    Mark as Compromised
                  </Button>
                </div>
                <div className="text-[10px] italic text-[#7a7060]">
                  Marking as compromised will deactivate the account, revoke all active sessions, and stamp <code>compromisedAt</code>. The flag transition + each cascaded action will share the same request ID in the audit log.
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Compromise confirmation */}
      {showCompromiseConfirm && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center"
          style={{ background: 'rgba(8,17,31,0.65)' }}
          onClick={() => setShowCompromiseConfirm(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-[14px] max-w-[460px] w-full mx-4 shadow-2xl">
            <div className="px-5 py-4 border-b" style={{ borderColor: '#e8e2d6', background: 'linear-gradient(180deg,#f2e9e6 0%,#ffffff 100%)' }}>
              <div className="flex items-center gap-2">
                <FiAlertOctagon className="text-[18px]" style={{ color: '#9a564a' }} />
                <h3 className="text-[15px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Mark Account as Compromised</h3>
              </div>
            </div>
            <div className="p-5 space-y-2 text-[12px] text-[#08111f]">
              <p>This will:</p>
              <ul className="list-disc list-inside text-[11.5px] space-y-1 text-[#7a7060]">
                <li>Set the flag status to <strong>compromised</strong></li>
                <li>Deactivate the user account (cannot sign in)</li>
                <li>Revoke all active sessions on every device</li>
                <li>Stamp <code>compromisedAt</code> so the UI shows a red “Compromised” pill</li>
                <li>Write three correlated audit rows under one request ID</li>
              </ul>
              <p className="text-[11.5px] italic mt-2">This action cannot be undone via the UI — to reactivate, you'd need to clear deactivation manually from the user detail modal.</p>
            </div>
            <div className="px-5 py-3 border-t flex items-center justify-end gap-2" style={{ borderColor: '#e8e2d6' }}>
              <Button variant="ghost" onClick={() => setShowCompromiseConfirm(null)}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => compromise(showCompromiseConfirm._id)}
                loading={busyFlag === showCompromiseConfirm._id}
              >
                Yes, mark as compromised
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
